import { useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { 
  Home, 
  TrendingUp, 
  Plus, 
  User, 
  Brain,
  Wrench,
  Bitcoin,
  Trophy,
  Landmark,
  DollarSign,
  Gamepad2,
  Clapperboard,
  Zap,
  MessageCircle,
  LineChart,
  Building2,
  Globe2,
  Briefcase,
  X,
  Newspaper,
  ChevronDown,
  Target,
  Sparkles,
  Mail,
  LogOut,
  Coins
} from 'lucide-react'
import WalletConnection from './WalletConnection'
import { useAgiAuthStore } from '../store/agiAuthStore'
import AgiLoginModal from './AgiLoginModal'

const mainNav = [
  { name: 'Home', href: '/', icon: Home },
  { name: 'Markets', href: '/markets', icon: TrendingUp },
  { name: 'Profile', href: '/profile', icon: User },
]

const createOptions = [
  { name: 'Prediction Market', href: '/create', icon: Target, description: 'Create a YES/NO prediction pool', color: 'text-cpx-light-blue', agiOnly: false },
  { name: 'Fantasy Sports', href: '/fantasy', icon: Gamepad2, description: 'Build a fantasy contest', color: 'text-emerald-400', agiOnly: false },
  { name: 'AGI Event', href: '/create/agi-event', icon: Sparkles, description: 'Manual market for @agi.live team', color: 'text-yellow-400', agiOnly: true },
]

const liveMarkets = [
  { name: 'Live Events', href: '/markets?live=true', icon: Zap, color: 'text-cpx-yellow', param: 'live' },
  { name: 'News Bot', href: '/markets?newsbot=true', icon: Newspaper, color: 'text-green-400', param: 'newsbot' },
  { name: 'Fantasy Football', href: '/markets?category=fantasy', icon: Gamepad2, color: 'text-emerald-400', param: 'fantasy' },
]

const topics = [
  { name: 'All Markets', href: '/markets', icon: TrendingUp, color: 'text-white', param: null, agiOnly: false },
  { name: 'Crypto', href: '/markets?category=crypto', icon: Bitcoin, color: 'text-orange-400', param: 'crypto', agiOnly: false },
  { name: 'Stocks (Soon)', href: null, icon: LineChart, color: 'text-gray-500', param: 'stocks', disabled: true, agiOnly: false },
  { name: 'Sports', href: '/markets?category=sports', icon: Trophy, color: 'text-blue-400', param: 'sports', agiOnly: false },
  { name: 'Business', href: '/markets?category=business', icon: Briefcase, color: 'text-indigo-400', param: 'business', agiOnly: false },
  { name: 'Real Assets (Soon)', href: null, icon: Building2, color: 'text-gray-500', param: 'real_assets', disabled: true, agiOnly: false },
  { name: 'World Events', href: '/markets?category=world_events', icon: Globe2, color: 'text-cyan-400', param: 'world_events', agiOnly: false },
  { name: 'Politics', href: '/markets?category=politics', icon: Landmark, color: 'text-red-400', param: 'politics', agiOnly: false },
  { name: 'Entertainment', href: '/markets?category=entertainment', icon: Clapperboard, color: 'text-pink-400', param: 'entertainment', agiOnly: false },
  { name: 'Social', href: '/markets?category=social', icon: MessageCircle, color: 'text-purple-400', param: 'social', agiOnly: false },
  { name: 'Other', href: '/markets?category=other', icon: Brain, color: 'text-gray-400', param: 'other', agiOnly: false },
  { name: 'AGI Events', href: '/markets?category=agi_manual', icon: Sparkles, color: 'text-yellow-400', param: 'agi_manual', agiOnly: true },
]

interface SidebarProps {
  isOpen: boolean
  onClose: () => void
}

export default function Sidebar({ isOpen, onClose }: SidebarProps) {
  const location = useLocation()
  const navigate = useNavigate()
  const [showCreateMenu, setShowCreateMenu] = useState(false)
  const [showAgiLoginModal, setShowAgiLoginModal] = useState(false)
  
  // AGI Auth
  const { agiUser, isAgiAuthenticated, logoutAgi } = useAgiAuthStore()
  const isAgiUser = isAgiAuthenticated && agiUser?.isAgi
  
  // Parse current filters from URL
  const params = new URLSearchParams(location.search)
  const currentCategory = params.get('category')
  const isLiveFilter = params.get('live') === 'true'
  const isNewsBotFilter = params.get('newsbot') === 'true'
  const isFantasyFilter = currentCategory === 'fantasy'
  
  // Filter options based on AGI user status
  const filteredCreateOptions = createOptions.filter(opt => !opt.agiOnly || isAgiUser)
  const filteredTopics = topics.filter(topic => !topic.agiOnly || isAgiUser)

  if (!isOpen) return null

  return (
    <div className="fixed left-0 top-0 h-full w-60 bg-[#001233] border-r border-[#052393]/30 flex flex-col z-40 transition-all duration-300">
      {/* Logo */}
      <div className="p-6 border-b border-[#052393]/30 relative">
        <Link to="/" className="flex items-center space-x-3">
          <img 
            src="/cpx-logo.jpg" 
            alt="CPX Logo" 
            className="w-12 h-12 rounded-lg shadow-lg shadow-[#2AAAEA]/30 object-contain"
          />
          <div className="flex flex-col">
            <span className="text-2xl font-black text-white tracking-tight">
              CPX
            </span>
            <span className="text-[9px] text-gray-400 uppercase tracking-widest font-medium">
              Your Pool, Your Rules
            </span>
          </div>
        </Link>
        
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-6 right-6 w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:text-white hover:bg-[#052393]/30 transition-all"
          aria-label="Close sidebar"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Main Navigation */}
      <div className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
        {mainNav.map((item) => {
          const isActive = location.pathname === item.href
          return (
            <Link
              key={item.name}
              to={item.href}
              className={`
                flex items-center space-x-3 px-3 py-2.5 rounded-lg text-sm font-semibold transition-all duration-150 relative group
                ${isActive
                  ? 'text-white bg-[#052393]/50'
                  : 'text-gray-300 hover:text-white hover:bg-[#052393]/20'
                }
              `}
            >
              <item.icon className={`w-5 h-5 ${isActive ? 'text-cpx-light-blue' : 'text-gray-500 group-hover:text-cpx-light-blue'}`} />
              <span>{item.name}</span>
              {isActive && (
                <motion.div
                  layoutId="sidebar-active"
                  className="absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b from-[#2AAAEA] to-[#FBB03B] rounded-r"
                  initial={false}
                  transition={{ type: "spring", stiffness: 300, damping: 30 }}
                />
              )}
            </Link>
          )
        })}
        
        {/* Create Button with Dropdown */}
        <div className="relative">
          <button
            onClick={() => setShowCreateMenu(!showCreateMenu)}
            className={`
              w-full flex items-center justify-between px-3 py-2.5 rounded-lg text-sm font-semibold transition-all duration-150 relative group
              ${showCreateMenu || location.pathname === '/create' || location.pathname === '/fantasy'
                ? 'text-white bg-[#052393]/50'
                : 'text-gray-300 hover:text-white hover:bg-[#052393]/20'
              }
            `}
          >
            <div className="flex items-center space-x-3">
              <Plus className={`w-5 h-5 ${showCreateMenu ? 'text-cpx-light-blue' : 'text-gray-500 group-hover:text-cpx-light-blue'}`} />
              <span>Create</span>
            </div>
            <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${showCreateMenu ? 'rotate-180 text-cpx-light-blue' : 'text-gray-500'}`} />
          </button>
          
          <AnimatePresence>
            {showCreateMenu && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.2 }}
                className="overflow-hidden"
              >
                <div className="mt-1 ml-3 space-y-1 border-l-2 border-[#052393]/40 pl-3">
                  {filteredCreateOptions.map((option) => (
                    <button
                      key={option.name}
                      onClick={() => {
                        navigate(option.href)
                        setShowCreateMenu(false)
                      }}
                      className={`
                        w-full flex items-start space-x-3 px-3 py-3 rounded-lg text-left transition-all duration-150 group
                        ${location.pathname === option.href
                          ? 'bg-[#052393]/50 text-white'
                          : 'hover:bg-[#052393]/30 text-gray-300 hover:text-white'
                        }
                      `}
                    >
                      <option.icon className={`w-5 h-5 mt-0.5 ${option.color}`} />
                      <div>
                        <div className="font-semibold text-sm">{option.name}</div>
                        <div className="text-xs text-gray-500">{option.description}</div>
                      </div>
                    </button>
                  ))}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Live Markets Section */}
        <div className="pt-6">
          <h3 className="px-3 text-xs font-bold text-gray-400 uppercase tracking-widest mb-3">
            LIVE
          </h3>
          {liveMarkets.map((item) => {
            const isActive = item.param === 'live' ? isLiveFilter : item.param === 'newsbot' ? isNewsBotFilter : item.param === 'fantasy' ? isFantasyFilter : false
            return (
              <Link
                key={item.name}
                to={item.href}
                className={`flex items-center space-x-3 px-3 py-2.5 rounded-lg text-sm font-semibold transition-all duration-150 relative group ${
                  isActive
                    ? 'text-white bg-[#052393]/50'
                    : 'text-gray-300 hover:text-white hover:bg-[#052393]/20'
                }`}
              >
                {isActive && (
                  <motion.div
                    layoutId="sidebar-category-active"
                    className="absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b from-[#FBB03B] to-[#009245] rounded-r"
                    initial={false}
                    transition={{ type: "spring", stiffness: 300, damping: 30 }}
                  />
                )}
                <div className={`w-6 h-6 rounded-md bg-[#052393]/30 flex items-center justify-center ${isActive ? item.color : 'text-gray-500'} ${isActive ? '' : 'group-hover:' + item.color}`}>
                  <item.icon className="w-4 h-4" />
                </div>
                <div className="flex-1">
                  <div className="text-sm">{item.name}</div>
                </div>
              </Link>
            )
          })}
        </div>
        
        {/* Topics Section */}
        <div className="pt-6">
          <h3 className="px-3 text-xs font-bold text-gray-400 uppercase tracking-widest mb-3">
            CATEGORIES
          </h3>
          {filteredTopics.map((topic) => {
            const isActive = topic.param === null 
              ? !currentCategory && !isLiveFilter 
              : currentCategory === topic.param
            
            const content = (
              <>
                {isActive && (
                  <motion.div
                    layoutId="sidebar-category-active"
                    className="absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b from-[#2AAAEA] to-[#FBB03B] rounded-r"
                    initial={false}
                    transition={{ type: "spring", stiffness: 300, damping: 30 }}
                  />
                )}
                <div className={`w-6 h-6 rounded-md bg-[#052393]/30 flex items-center justify-center ${isActive ? topic.color : 'text-gray-500'} ${isActive ? '' : 'group-hover:' + topic.color}`}>
                  <topic.icon className="w-4 h-4" />
                </div>
                <div className="flex-1">
                  <div className="text-sm">{topic.name}</div>
                </div>
              </>
            );
            
            if (topic.disabled || !topic.href) {
              return (
                <div
                  key={topic.name}
                  className="flex items-center space-x-3 px-3 py-2.5 rounded-lg text-sm font-semibold relative cursor-not-allowed opacity-50"
                >
                  {content}
                </div>
              );
            }
            
            return (
              <Link
                key={topic.name}
                to={topic.href}
                className={`flex items-center space-x-3 px-3 py-2.5 rounded-lg text-sm font-semibold transition-all duration-150 relative group ${
                  isActive
                    ? 'text-white bg-[#052393]/50'
                    : 'text-gray-300 hover:text-white hover:bg-[#052393]/20'
                }`}
              >
                {content}
              </Link>
            )
          })}
        </div>
      </div>

      {/* Bottom Section - Wallet Connection + AGI Login */}
      <div className="p-4 border-t border-[#052393]/30 space-y-3">
        <WalletConnection />
        
        {/* AGI Login Section */}
        {isAgiAuthenticated && agiUser ? (
          <div className="bg-gradient-to-r from-yellow-500/10 to-orange-500/10 rounded-lg p-3 border border-yellow-500/30">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center space-x-2">
                <Sparkles className="w-4 h-4 text-yellow-400" />
                <span className="text-xs font-bold text-yellow-400">AGI USER</span>
              </div>
              <button
                onClick={logoutAgi}
                className="text-gray-400 hover:text-white transition-colors"
                title="Logout AGI"
              >
                <LogOut className="w-4 h-4" />
              </button>
            </div>
            <div className="text-sm text-white font-medium truncate">{agiUser.displayName}</div>
            <div className="flex items-center space-x-1 mt-1">
              <Coins className="w-3 h-3 text-yellow-400" />
              <span className="text-xs text-yellow-400 font-bold">{agiUser.credits} credits</span>
            </div>
            {agiUser.isAdmin && (
              <div className="mt-2 text-xs text-purple-400 font-semibold">🛡️ Admin</div>
            )}
          </div>
        ) : (
          <button
            onClick={() => setShowAgiLoginModal(true)}
            className="w-full flex items-center justify-center space-x-2 px-4 py-2.5 bg-gradient-to-r from-yellow-500/20 to-orange-500/20 hover:from-yellow-500/30 hover:to-orange-500/30 border border-yellow-500/30 rounded-lg transition-all"
          >
            <Mail className="w-4 h-4 text-yellow-400" />
            <span className="text-sm font-semibold text-yellow-400">Login with Email</span>
          </button>
        )}
      </div>
      
      {/* AGI Login Modal */}
      <AgiLoginModal 
        isOpen={showAgiLoginModal} 
        onClose={() => setShowAgiLoginModal(false)} 
      />
    </div>
  )
}

